import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
export function HeroSection() {
  const ref = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  // Parallax: background moves at 0.5x scroll speed
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  return <section ref={ref} className="relative h-screen w-full overflow-hidden bg-fight-black flex items-center justify-center" data-id="element-100">
      {/* Parallax Background */}
      <motion.div style={{
      y: bgY
    }} className="absolute inset-0 scale-110" data-id="element-101">
        <div className="w-full h-full bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1599058945522-28d584b6f0ff?q=80&w=2069&auto=format&fit=crop")',
        filter: 'grayscale(100%) contrast(110%) brightness(0.5)',
        opacity: 0.5
      }} data-id="element-102" />
      </motion.div>

      {/* Cage Grid Overlay */}
      <div className="absolute inset-0 bg-cage-grid opacity-15 pointer-events-none" data-id="element-103" />

      {/* Vignette */}
      <div className="absolute inset-0 bg-vignette pointer-events-none" data-id="element-104" />

      {/* Soft spotlight from top */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full pointer-events-none" style={{
      background: 'radial-gradient(ellipse, rgba(255,255,255,0.04) 0%, transparent 70%)'
    }} data-id="element-105" />

      {/* Yellow atmospheric glow — bottom */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[700px] h-[350px] rounded-full pointer-events-none" style={{
      background: 'radial-gradient(ellipse, rgba(245,230,20,0.04) 0%, transparent 70%)',
      filter: 'blur(40px)'
    }} data-id="element-106" />

      {/* Fog */}
      <div className="fog-container" data-id="element-107">
        <div className="fog-img" data-id="element-108" />
        <div className="fog-img-2" data-id="element-109" />
      </div>

      {/* Content with subtle parallax */}
      <motion.div style={{
      y: contentY,
      opacity
    }} className="relative z-10 text-center px-4 max-w-7xl mx-auto flex flex-col items-center" data-id="element-110">
        {/* Logo */}
        <motion.img src="https://cdn.magicpatterns.com/uploads/igMiamPeru7PGzkV6cwrzL/logo_ohne-hintergrund-klein.png" alt="Zehners Merch" initial={{
        opacity: 0,
        scale: 0.85
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.1,
        duration: 0.9,
        ease: 'easeOut'
      }} className="h-28 md:h-40 w-auto object-contain mb-6" style={{
        filter: 'drop-shadow(0 0 40px rgba(245,230,20,0.35)) drop-shadow(0 0 80px rgba(245,230,20,0.1))'
      }} data-id="element-111" />

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5,
        duration: 0.8
      }} className="font-bebas text-2xl md:text-4xl tracking-widest mb-4 yellow-glow-text" data-id="element-112">
          WEAR YOUR FIGHT
        </motion.p>

        <motion.h1 initial={{
        opacity: 0,
        scale: 0.92
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.2,
        duration: 0.9,
        ease: 'easeOut'
      }} className="text-6xl md:text-8xl lg:text-[110px] font-bebas leading-[0.85] tracking-tighter mb-6 glitch-text" style={{
        color: '#e0e0e0'
      }} data-text="ZEHNERS MERCH" data-id="element-113">
          ZEHNERS MERCH
        </motion.h1>

        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.8,
        duration: 0.8
      }} className="font-barlow text-lg md:text-xl tracking-widest uppercase max-w-2xl mb-10" style={{
        color: '#555'
      }} data-id="element-114">
          Premium MMA Merchandise. Limited Drops. No Compromises.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1,
        duration: 0.8
      }} className="flex flex-col md:flex-row gap-6" data-id="element-115">
          <a href="#shop" className="px-10 py-4 font-bebas text-xl tracking-wider transition-all duration-300 min-w-[200px]" style={{
          background: 'rgba(153,27,27,0.8)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(153,27,27,0.4)',
          color: '#e0e0e0',
          clipPath: 'polygon(10% 0, 100% 0, 100% 100%, 0% 100%)',
          boxShadow: '0 0 20px rgba(153,27,27,0.2)'
        }} onMouseEnter={e => {
          ;
          (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(127,29,29,0.9)';
          (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 0 30px rgba(153,27,27,0.35)';
        }} onMouseLeave={e => {
          ;
          (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(153,27,27,0.8)';
          (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 0 20px rgba(153,27,27,0.2)';
        }} data-id="element-116">
            SHOP NOW
          </a>
          <a href="#drops" className="px-10 py-4 font-bebas text-xl tracking-wider transition-all duration-300 min-w-[200px]" style={{
          background: 'rgba(245,230,20,0.05)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(245,230,20,0.25)',
          color: 'rgba(245,230,20,0.85)',
          boxShadow: '0 0 16px rgba(245,230,20,0.06)'
        }} onMouseEnter={e => {
          ;
          (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(245,230,20,0.12)';
          (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 0 24px rgba(245,230,20,0.15)';
        }} onMouseLeave={e => {
          ;
          (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(245,230,20,0.05)';
          (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 0 16px rgba(245,230,20,0.06)';
        }} data-id="element-117">
            VIEW DROPS
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1,
      y: [0, 10, 0]
    }} transition={{
      delay: 2,
      duration: 2,
      repeat: Infinity
    }} className="absolute bottom-10 left-1/2 -translate-x-1/2" style={{
      color: 'rgba(245,230,20,0.4)'
    }} data-id="element-118">
        <ChevronDown className="w-8 h-8" data-id="element-119" />
      </motion.div>
    </section>;
}